import React, { useEffect } from 'react';

import { NextPage } from 'next';

import { Breadcrumb, BreadcrumbWithUrl } from '@domains/breadcrumbs';
import { ArticleDetailsPageProps } from '@domains/view';

import { sendShowProductsEvent } from '@utils/analytics';
import { makeAllArticlesRoute, makeNewsArticlesRoute, makeNewsPromotionsRoute } from '@utils/route';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { ArticleDatesRange } from '@components/article/ArticleDatesRange/ArticleDatesRange';
import { ArticlesSlider } from '@components/article/ArticlesSlider/ArticlesSlider';
import { Badge, BadgeVariants } from '@components/common/Badge/Badge';
import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/common/Container/Container';
import { ImagePreload } from '@components/common/ImagePreload/ImagePreload';
import { Picture } from '@components/common/Picture/Picture';
import { WysiwygPresenter } from '@components/common/WysiwygPresenter/WysiwygPresenter';
import { ProductCard } from '@components/product/ProductCard/ProductCard';

import styles from './Article.module.scss';

export const Article: NextPage<ArticleDetailsPageProps> = (props) => {
    const {
        data: { dates, text, title, title_image, type, other_news, products, seo },
    } = props;

    const isNews = type === 'news';

    const badgeText = isNews ? 'Новости' : 'Акции';
    const badgeVariant = isNews ? 'isNews' : 'isPromo';

    const breadcrumbs: Breadcrumb[] = [
        {
            url: makeAllArticlesRoute(),
            caption: 'Акции и новости',
        },
        {
            caption: title,
        },
    ];

    const mobileButton: BreadcrumbWithUrl = {
        url: isNews ? makeNewsArticlesRoute() : makeNewsPromotionsRoute(),
        caption: isNews ? 'Новости' : 'Акции',
    };

    useEffect(() => {
        if (products.length === 0) return;
        sendShowProductsEvent(products);
    }, [products]);

    return (
        <DefaultLayout {...seo}>
            <div className={styles.root}>
                <Container>
                    <Breadcrumbs items={breadcrumbs} mobileButton={mobileButton} isWithHomeCrumb />
                    <div className={styles.contentWrapper}>
                        <h1
                            className={styles.title}
                            dangerouslySetInnerHTML={{ __html: title }}
                        ></h1>
                        <div className={styles.dateRow}>
                            <Badge variant={badgeVariant as BadgeVariants} customText={badgeText} />
                            <p className={styles.dateText}>
                                <ArticleDatesRange dates={dates} type={type} />
                            </p>
                        </div>
                        {title_image && (
                            <>
                                <ImagePreload sources={title_image} />
                                <Picture
                                    priority
                                    classes={{ picture: styles.picture }}
                                    className={styles.mainImage}
                                    sources={title_image}
                                />
                            </>
                        )}
                        {text && <WysiwygPresenter className={styles.text} content={text} />}
                    </div>

                    <div className={styles.grid}>
                        {products.map((product) => (
                            <ProductCard
                                key={product.slug}
                                product={product}
                                className={styles.slide}
                            />
                        ))}
                    </div>
                </Container>
                <ArticlesSlider
                    articles={other_news}
                    title="Другие новости и акции"
                    className={styles.slider}
                />
            </div>
        </DefaultLayout>
    );
};
